import { sampleSprite } from './sprite';
import type { Actor, Bounds, Theme } from './types';
import type { Effects } from './effects';

const STYLES = `
  :host { all: initial !important; position: fixed !important; display: block !important; left: var(--animate-left, 0px) !important; top: var(--animate-top, 0px) !important; width: var(--animate-width, 100vw) !important; height: var(--animate-height, 100vh) !important; pointer-events: none !important; z-index: 2147483000 !important; overflow: hidden !important; contain: strict !important; color-scheme: light !important; }
  *, *::before, *::after { box-sizing: border-box; }
  canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
  button { all: initial; position: absolute; left: 0; top: 0; display: block; cursor: pointer; pointer-events: auto; border-radius: 35%; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
  button:focus-visible { outline: 2px dashed #ed6b27; outline-offset: 4px; background: #ed6b2712; }
  button:disabled { cursor: default; }
`;

export class Renderer {
  readonly host = document.createElement('animate-companion');
  private canvas = document.createElement('canvas');
  private ctx: CanvasRenderingContext2D;
  private shadow = this.host.attachShadow({ mode: 'open' });
  private buttons = new Map<number, HTMLButtonElement>();
  private images = new Map<string, HTMLImageElement>();
  private width = 0;
  private height = 0;
  private dpr = 1;
  private destroyed = false;
  private abortLoad: (() => void) | undefined;

  constructor(
    private theme: Theme,
    private onClick: (id: number) => void,
  ) {
    const style = document.createElement('style');
    style.textContent = STYLES;
    this.canvas.setAttribute('aria-hidden', 'true');
    this.shadow.append(style, this.canvas);
    const context = this.canvas.getContext('2d', { alpha: true });
    if (!context) throw new Error('Animate requires a browser with Canvas 2D support.');
    this.ctx = context;
    document.body.append(this.host);
  }

  async load(assetBase: string): Promise<void> {
    const controller = new AbortController();
    this.abortLoad = () => controller.abort();
    const load = (image: HTMLImageElement, filename: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const finish = (error?: Error) => {
          clearTimeout(timer);
          image.onload = null;
          image.onerror = null;
          controller.signal.removeEventListener('abort', abort);
          error ? reject(error) : resolve();
        };
        const abort = () => finish(new Error('Animate: asset loading cancelled.'));
        const timer = setTimeout(
          () => finish(new Error(`Animate: timed out loading ${filename}.`)),
          15000,
        );
        controller.signal.addEventListener('abort', abort, { once: true });
        image.onload = () => finish();
        image.onerror = () =>
          finish(new Error(`Animate: could not load ${filename} from ${assetBase}`));
        image.src = new URL(filename, assetBase.endsWith('/') ? assetBase : `${assetBase}/`).href;
      });
    await Promise.all(
      Object.entries(this.theme.assets).map(([key, filename]) => {
        const image = new Image();
        this.images.set(key, image);
        return load(image, filename);
      }),
    );
    this.abortLoad = undefined;
  }

  resize(bounds: Bounds): void {
    // Custom properties survive the host's `all: initial` isolation reset.
    this.host.style.setProperty('--animate-left', `${bounds.left}px`);
    this.host.style.setProperty('--animate-top', `${bounds.top}px`);
    this.host.style.setProperty('--animate-width', `${bounds.width}px`);
    this.host.style.setProperty('--animate-height', `${bounds.height}px`);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (this.width === bounds.width && this.height === bounds.height && this.dpr === dpr) return;
    this.width = bounds.width;
    this.height = bounds.height;
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.round(bounds.width * dpr));
    this.canvas.height = Math.max(1, Math.round(bounds.height * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx.imageSmoothingQuality = 'high';
  }

  draw(
    actors: readonly Actor[],
    effects: Effects,
    canInteract: boolean,
    speech?: string,
    drawOverlay?: (ctx: CanvasRenderingContext2D) => void,
  ): void {
    if (this.destroyed) return;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);
    for (const summon of effects.summons) {
      const sheet = this.theme.summon;
      const summonImage = this.images.get('summon');
      if (!sheet || !summonImage) continue;
      const enter = Math.min(1, summon.age * 3);
      const breath = 1 + Math.sin(summon.age * 2.5) * 0.025;
      const cellW = summonImage.naturalWidth / sheet.columns;
      const cellH = summonImage.naturalHeight / sheet.rows;
      const frame = summon.age < 0.4 ? 2 : Math.floor(summon.age * 1.1) % 2;
      ctx.save();
      ctx.globalAlpha = enter;
      ctx.translate(summon.x, summon.y);
      ctx.scale(1, breath);
      ctx.drawImage(
        summonImage,
        (frame % sheet.columns) * cellW,
        Math.floor(frame / sheet.columns) * cellH,
        cellW,
        cellH,
        -summon.size / 2,
        -summon.size * sheet.baseline,
        summon.size,
        summon.size,
      );
      ctx.restore();
    }
    for (const log of effects.logs) {
      const logImage = this.images.get('log');
      if (!logImage) continue;
      ctx.save();
      ctx.globalAlpha = Math.min(1, (log.life - log.age) / 0.6);
      ctx.translate(log.x, log.y);
      ctx.rotate(Math.sin(log.age * 18) * Math.exp(-log.age * 5) * 0.09);
      ctx.drawImage(logImage, -log.size / 2, -log.size * 0.946, log.size, log.size);
      ctx.restore();
    }
    for (const ghost of effects.afterimages) {
      this.drawActor({ ...ghost.actor, opacity: (1 - ghost.age / ghost.life) * 0.24 }, true);
    }
    for (const actor of actors) this.drawActor(actor);
    for (const prop of effects.props) {
      const source = this.images.get(prop.asset);
      const atlas = this.theme.atlases?.[prop.asset];
      const rect = atlas?.frames[prop.frame];
      if (!source || !atlas || !rect) continue;
      const scale = prop.size / atlas.unitHeight;
      ctx.save();
      ctx.globalAlpha = prop.opacity;
      ctx.translate(prop.x, prop.y);
      ctx.rotate(prop.rotation);
      ctx.drawImage(
        source,
        ...rect,
        (-rect[2] * scale) / 2,
        (-rect[3] * scale) / 2,
        rect[2] * scale,
        rect[3] * scale,
      );
      ctx.restore();
    }
    effects.draw(ctx);
    if (drawOverlay) {
      ctx.save();
      drawOverlay(ctx);
      ctx.restore();
    }
    if (
      speech &&
      actors[0]?.opacity &&
      Math.abs(actors[0].rotation ?? 0) < 0.8 &&
      !actors[0].occlusion
    ) {
      const actor = actors[0];
      const reserved = this.theme.clips[actor.pose]?.speechClearance;
      const clearance = effects.charge
        ? effects.charge.y - effects.charge.radius * 2 - 38
        : reserved
          ? actor.y - actor.size * reserved - 38
          : undefined;
      this.drawSpeech(actors[0], speech, clearance);
    }
    this.syncButtons(actors, canInteract);
  }

  private drawActor(actor: Actor, ghost = false): void {
    const ctx = this.ctx;
    const { rect, scale, asset, pivot } = sampleSprite(actor, this.theme);
    const source = this.images.get(asset);
    if (!source) return;
    const breath =
      actor.pose === 'idle' || actor.pose === 'perch'
        ? Math.sin(actor.age * 2.2) * 0.008
        : actor.pose === 'laugh'
          ? Math.sin(actor.poseTime * 14) * 0.018
          : 0;
    ctx.save();
    if (actor.occlusion) {
      const box = actor.occlusion;
      ctx.beginPath();
      ctx.rect(0, 0, this.width, this.height);
      ctx.rect(box.left, box.top, box.width, box.height);
      ctx.clip('evenodd');
    }
    ctx.globalAlpha = actor.opacity;
    if (
      !ghost &&
      !actor.rotation &&
      !actor.occlusion &&
      !['jump', 'dodge', 'fly', 'float', 'phase', 'peek'].includes(actor.pose)
    ) {
      ctx.fillStyle = 'rgba(34,28,23,.12)';
      ctx.beginPath();
      ctx.ellipse(actor.x, actor.y + 1, actor.size * 0.19, 4, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.translate(actor.x, actor.y);
    ctx.rotate(actor.rotation ?? 0);
    ctx.scale(actor.facing, 1 + breath);
    if (actor.clone) ctx.globalAlpha *= 0.9;
    ctx.drawImage(
      source,
      ...rect,
      -pivot.x * scale,
      -pivot.y * scale,
      rect[2] * scale,
      rect[3] * scale,
    );
    if (actor.pose === 'ramen') this.drawSteam(actor);
    ctx.restore();
  }

  private drawSteam(actor: Actor): void {
    const ctx = this.ctx;
    ctx.lineWidth = 1.3;
    ctx.lineCap = 'round';
    for (let i = 0; i < 3; i++) {
      const progress = (actor.poseTime * 0.65 + i / 3) % 1;
      const x = actor.size * 0.16 + Math.sin(progress * 5 + i) * 3;
      const y = -actor.size * (0.33 + progress * 0.18);
      ctx.strokeStyle = `rgba(255,255,255,${Math.sin(progress * Math.PI) * 0.75})`;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.bezierCurveTo(x - 4, y - 4, x + 4, y - 8, x, y - 13);
      ctx.stroke();
    }
  }

  private drawSpeech(actor: Actor, text: string, clearance = Infinity): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.font = '600 11px system-ui, sans-serif';
    const width = ctx.measureText(text).width + 24;
    const x = Math.max(6, Math.min(this.width - width - 6, actor.x - width / 2));
    const y = Math.max(8, Math.min(actor.y - actor.size - 24, clearance));
    ctx.fillStyle = '#292720';
    ctx.beginPath();
    ctx.roundRect(x, y, width, 29, 8);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x + width / 2 - 4, y + 28);
    ctx.lineTo(x + width / 2, y + 33);
    ctx.lineTo(x + width / 2 + 4, y + 28);
    ctx.fill();
    ctx.fillStyle = '#fffaf4';
    ctx.fillText(text, x + 12, y + 18);
    ctx.restore();
  }

  private syncButtons(actors: readonly Actor[], canInteract: boolean): void {
    const activeIds = new Set(actors.map((actor) => actor.id));
    for (const [id, button] of this.buttons) {
      if (!activeIds.has(id)) {
        button.remove();
        this.buttons.delete(id);
      }
    }
    for (const actor of actors) {
      let button = this.buttons.get(actor.id);
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.setAttribute(
          'aria-label',
          actor.clone ? 'Faire disparaître le clone' : this.theme.primaryLabel,
        );
        button.dataset.actor = String(actor.id);
        button.dataset.clone = String(actor.clone);
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.onClick(actor.id);
        });
        this.shadow.append(button);
        this.buttons.set(actor.id, button);
      }
      button.disabled = !actor.clone && !canInteract;
      button.dataset.pose = actor.pose;
      button.dataset.theme = this.theme.id;
      button.style.visibility =
        actor.opacity === 0 || Math.abs(actor.rotation ?? 0) > 0.8 || actor.occlusion
          ? 'hidden'
          : 'visible';
      button.style.width = `${actor.size * 0.54}px`;
      button.style.height = `${actor.size * 0.9}px`;
      button.style.transform = `translate(${actor.x - actor.size * 0.27}px,${actor.y - actor.size * 0.9}px)`;
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.abortLoad?.();
    this.host.remove();
    this.buttons.clear();
    this.images.clear();
  }
}
